import { useState, useEffect, useCallback, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import { API_CONFIG } from '../config';
import { CameraFacingMode, getNextFacingMode, getReplacementVideoStreamAfterStoppingCurrent } from '../utils/camera';

export type CallRole = 'host' | 'guest';
export type CallState = 'idle' | 'waiting' | 'connecting' | 'connected' | 'ended';

export const useAnonymousCall = () => {
  const [callState, setCallState] = useState<CallState>('idle');
  const [callKey, setCallKey] = useState<string | null>(null);
  const [role, setRole] = useState<CallRole | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [callTimer, setCallTimer] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [cameraFacingMode, setCameraFacingMode] = useState<CameraFacingMode>('user');
  const [remoteName, setRemoteName] = useState<string>('');
  
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const callTimerRef = useRef<NodeJS.Timeout | null>(null);
  const callKeyRef = useRef<string | null>(null);
  const roleRef = useRef<CallRole | null>(null);
  
  // Queues for offers/answers/ICE candidates that arrive before peer connection is ready
  const pendingOfferRef = useRef<{ fromConnectionId: string; offer: any; callType: string } | null>(null);
  const pendingIceCandidatesRef = useRef<{ fromConnectionId: string; candidate: any }[]>([]);
  const handlersRegisteredRef = useRef(false);
  
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const cameraFacingModeRef = useRef<CameraFacingMode>('user');

  const updateCameraFacingMode = useCallback((nextFacingMode: CameraFacingMode) => {
    cameraFacingModeRef.current = nextFacingMode;
    setCameraFacingMode(nextFacingMode);
  }, []);

  useEffect(() => {
    callKeyRef.current = callKey;
  }, [callKey]);

  useEffect(() => {
    roleRef.current = role;
  }, [role]);

  // Setup SignalR connection (anonymous, no auth token)
  const setupConnection = useCallback(async () => {
    if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
      return connectionRef.current;
    }

    const hubUrl = API_CONFIG.chatHubURL;
    console.log('Setting up SignalR connection to:', hubUrl);

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        // No access token for anonymous connection
        skipNegotiation: false,
        // Explicitly enable WebSocket transport for HTTPS compatibility
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
        // Allow credentials for CORS
        withCredentials: true
      })
      .withAutomaticReconnect([0, 3000, 5000, 10000, 15000, 30000]) // Retry with backoff
      .build();

    connection.onreconnected(async () => {
      try {
        if (roleRef.current === 'host' && callKeyRef.current) {
          await connection.invoke('RefreshAnonymousCallSession', callKeyRef.current);
        }
      } catch (err) {
        console.error('Failed to refresh anonymous session after reconnect:', err);
      }
    });

    try {
      await connection.start();
      console.log('SignalR connection established successfully');
      connectionRef.current = connection;
      return connection;
    } catch (err) {
      console.error('SignalR connection error:', err);
      console.error('Attempted URL:', hubUrl);
      console.error('Connection error details:', {
        message: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : undefined
      });
      setError('Failed to connect to server. Please check your network connection and ensure the API is accessible.');
      throw err;
    }
  }, []);

  // Setup peer connection for WebRTC
  const setupPeerConnection = useCallback((callKeyParam: string) => {
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
    }

    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
      ]
    });

    pc.onicecandidate = (event) => {
      if (event.candidate && connectionRef.current) {
        // Use roleRef.current to avoid stale closure
        const currentRole = roleRef.current;
        const targetConnectionId = currentRole === 'host' 
          ? sessionStorage.getItem('anonymousCallGuestConnectionId')
          : sessionStorage.getItem('anonymousCallHostConnectionId');
        
        console.log('ICE candidate generated, role:', currentRole, 'target:', targetConnectionId);
        if (targetConnectionId) {
          connectionRef.current.invoke('AnonymousIceCandidate', callKeyParam, targetConnectionId, event.candidate)
            .catch(err => console.error('Error sending ICE candidate:', err));
        } else {
          console.warn('No target connection ID for ICE candidate, role:', currentRole);
        }
      }
    };

    pc.ontrack = (event) => {
      console.log('Remote track received:', event.track.kind, 'streams:', event.streams.length);
      const stream = event.streams[0];
      if (!stream) {
        console.warn('No stream in ontrack event, creating new stream from track');
        const newStream = new MediaStream([event.track]);
        remoteStreamRef.current = newStream;
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = newStream;
        }
        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = newStream;
        }
        return;
      }
      remoteStreamRef.current = stream;
      
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = stream;
      }
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = stream;
      }
    };

    pc.onconnectionstatechange = () => {
      console.log('Connection state:', pc.connectionState);
      if (pc.connectionState === 'connected') {
        setCallState('connected');
        startCallTimer();
      } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        setError('Connection lost');
      }
    };

    peerConnectionRef.current = pc;

    // Process any queued ICE candidates
    if (pendingIceCandidatesRef.current.length > 0) {
      console.log('Processing', pendingIceCandidatesRef.current.length, 'queued ICE candidates');
      const candidates = [...pendingIceCandidatesRef.current];
      pendingIceCandidatesRef.current = [];
      candidates.forEach(({ candidate }) => {
        pc.addIceCandidate(new RTCIceCandidate(candidate))
          .catch(err => console.error('Error adding queued ICE candidate:', err));
      });
    }

    return pc;
  }, []);

  // Start call timer
  const startCallTimer = () => {
    setCallTimer(0);
    if (callTimerRef.current) clearInterval(callTimerRef.current);
    callTimerRef.current = setInterval(() => setCallTimer(t => t + 1), 1000);
  };

  // Stop call timer
  const stopCallTimer = () => {
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
    setCallTimer(0);
  };

  // Get user media
  const getUserMedia = useCallback(async (videoEnabled: boolean = true) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: videoEnabled ? { facingMode: cameraFacingMode } : false,
        audio: true
      });
      
      localStreamRef.current = stream;
      
      if (localVideoRef.current && videoEnabled) {
        localVideoRef.current.srcObject = stream;
      }
      
      return stream;
    } catch (err: any) {
      console.error('Error getting user media:', err);
      
      let errorMessage = 'Failed to access camera/microphone';
      if (err.name === 'NotFoundError') {
        errorMessage = 'Camera or microphone not found. Please connect a device.';
      } else if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage = 'Permission denied. Please allow camera and microphone access.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Camera/microphone is already in use by another application.';
      }
      
      setError(errorMessage);
      throw err;
    }
  }, [cameraFacingMode]);

  // Create a new call session (Host)
  const createSession = useCallback(async (callKeyInput: string, hostName: string = 'Host') => {
    try {
      setError(null);
      setCallState('waiting');
      setRole('host');
      
      console.log('Creating call session with key:', callKeyInput);
      const connection = await setupConnection();
      
      console.log('Invoking CreateAnonymousCallSession...');
      const result = await connection.invoke('CreateAnonymousCallSession', callKeyInput, hostName);
      
      console.log('Session creation result:', result);
      if (result.success) {
        setCallKey(result.callKey);
        setupSignalRHandlers(connection, result.callKey);
        
        // Don't request media yet - wait for guest to join
        // Media will be requested when 'AnonymousGuestJoined' event fires
        
        return result.callKey;
      } else {
        const errorMsg = result.message || 'Failed to create session';
        console.error('Session creation failed:', errorMsg);
        setError(errorMsg);
        setCallState('idle');
        return null;
      }
    } catch (err: any) {
      console.error('Error creating session:', err);
      const errorMsg = err.message || err.toString() || 'Failed to create session';
      setError(errorMsg);
      setCallState('idle');
      return null;
    }
  }, [setupConnection]);

  // Join an existing session (Guest)
  const joinSession = useCallback(async (callKeyParam: string, guestName: string = 'Guest') => {
    try {
      setError(null);
      setCallState('connecting');
      setRole('guest');
      setCallKey(callKeyParam);
      
      const connection = await setupConnection();
      
      const result = await connection.invoke('JoinAnonymousCallSession', callKeyParam, guestName);
      
      if (result.success) {
        // Store host connection ID for signaling
        sessionStorage.setItem('anonymousCallHostConnectionId', result.hostConnectionId);
        setRemoteName(result.hostName || 'Host');
        
        setupSignalRHandlers(connection, callKeyParam);
        
        try {
          // Get media and setup peer connection
          const stream = await getUserMedia();
          const pc = setupPeerConnection(callKeyParam);
          
          // Add local tracks to peer connection
          stream.getTracks().forEach(track => pc.addTrack(track, stream));
          
          // Create and send offer to host
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          
          await connection.invoke('AnonymousCallOffer', callKeyParam, result.hostConnectionId, offer, 'video');
          
          return true;
        } catch (mediaErr: any) {
          console.error('Failed to get media:', mediaErr);
          setError('Failed to access camera/microphone. Please grant permissions and ensure devices are connected.');
          setCallState('idle');
          return false;
        }
      } else {
        setError(result.message || 'Failed to join session');
        setCallState('idle');
        return false;
      }
    } catch (err: any) {
      console.error('Error joining session:', err);
      setError(err.message || 'Failed to join session');
      setCallState('idle');
      return false;
    }
  }, [setupConnection, getUserMedia, setupPeerConnection]);

  // Process an offer on a ready peer connection
  const processOffer = async (pc: RTCPeerConnection, callKeyParam: string, fromConnectionId: string, offer: any, callType: string) => {
    try {
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      
      await connectionRef.current?.invoke('AnonymousCallAnswer', callKeyParam, fromConnectionId, answer);
      console.log('Offer processed and answer sent');
    } catch (err) {
      console.error('Error processing offer:', err);
    }
  };

  // Setup SignalR event handlers
  const setupSignalRHandlers = (connection: signalR.HubConnection, callKeyParam: string) => {
    // Prevent duplicate handler registration
    if (handlersRegisteredRef.current) {
      connection.off('AnonymousGuestJoined');
      connection.off('AnonymousCallOffer');
      connection.off('AnonymousCallAnswer');
      connection.off('AnonymousIceCandidate');
      connection.off('AnonymousCallEnded');
    }
    handlersRegisteredRef.current = true;

    // Guest joined (for host)
    connection.on('AnonymousGuestJoined', async (data: any) => {
      console.log('Guest joined:', data);
      setRemoteName(data.guestName || 'Guest');
      sessionStorage.setItem('anonymousCallGuestConnectionId', data.guestConnectionId);
      
      try {
        // Host prepares to receive offer - request media now
        const stream = await getUserMedia();
        const pc = setupPeerConnection(callKeyParam);
        
        // IMPORTANT: Add tracks BEFORE processing any queued offer
        // so that createAnswer() includes the host's media in the SDP
        stream.getTracks().forEach(track => {
          pc.addTrack(track, stream);
          console.log('Host added track:', track.kind);
        });
        
        // Now process any queued offer that arrived while getUserMedia was pending
        if (pendingOfferRef.current) {
          const { fromConnectionId, offer, callType } = pendingOfferRef.current;
          pendingOfferRef.current = null;
          console.log('Processing queued offer after tracks added');
          await processOffer(pc, callKeyParam, fromConnectionId, offer, callType);
        }
      } catch (err: any) {
        console.error('Failed to get media for host:', err);
        setError('Failed to access camera/microphone. Please grant permissions.');
      }
    });

    // Call offer received
    connection.on('AnonymousCallOffer', async (fromConnectionId: string, offer: any, callType: string) => {
      console.log('Received call offer, peer connection ready:', !!peerConnectionRef.current);
      
      const pc = peerConnectionRef.current;
      if (!pc) {
        // Queue the offer — host hasn't finished getUserMedia yet
        console.log('Peer connection not ready, queuing offer');
        pendingOfferRef.current = { fromConnectionId, offer, callType };
        return;
      }
      
      await processOffer(pc, callKeyParam, fromConnectionId, offer, callType);
    });

    // Call answer received
    connection.on('AnonymousCallAnswer', async (fromConnectionId: string, answer: any) => {
      console.log('Received call answer');
      
      const pc = peerConnectionRef.current;
      if (pc) {
        await pc.setRemoteDescription(new RTCSessionDescription(answer));
        setCallState('connected');
      }
    });

    // ICE candidate received
    connection.on('AnonymousIceCandidate', async (fromConnectionId: string, candidate: any) => {
      const pc = peerConnectionRef.current;
      if (pc && candidate) {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (err) {
          console.error('Error adding ICE candidate:', err);
        }
      } else if (candidate) {
        // Queue ICE candidate — peer connection not ready yet
        console.log('Peer connection not ready, queuing ICE candidate');
        pendingIceCandidatesRef.current.push({ fromConnectionId, candidate });
      }
    });

    // Call ended
    connection.on('AnonymousCallEnded', (endedSessionId: string) => {
      if (endedSessionId === callKeyParam) {
        cleanup();
        setCallState('ended');
      }
    });
  };

  // End call
  const endCall = useCallback(async () => {
    if (callKey && connectionRef.current) {
      try {
        await connectionRef.current.invoke('EndAnonymousCall', callKey);
      } catch (err) {
        console.error('Error ending call:', err);
      }
    }
    cleanup();
    setCallState('ended');
  }, [callKey]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  }, []);

  // Toggle video
  const toggleVideo = useCallback(() => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOff(!videoTrack.enabled);
      }
    }
  }, []);

  // Switch front/rear camera while keeping the active peer connection alive
  const switchCamera = useCallback(async () => {
    if (!localStreamRef.current || !peerConnectionRef.current) return;

    const currentVideoTrack = localStreamRef.current.getVideoTracks()[0];
    const nextFacingMode = getNextFacingMode(cameraFacingModeRef.current);
    try {
      const replacementStream = await getReplacementVideoStreamAfterStoppingCurrent(nextFacingMode, currentVideoTrack);
      const replacementTrack = replacementStream.getVideoTracks()[0];
      if (!replacementTrack) return;

      replacementTrack.enabled = !isVideoOff;
      const sender = peerConnectionRef.current.getSenders().find(s => s.track?.kind === 'video');
      if (sender) {
        await sender.replaceTrack(replacementTrack);
      }

      const oldVideoTracks = localStreamRef.current.getVideoTracks();
      oldVideoTracks.forEach(track => {
        localStreamRef.current?.removeTrack(track);
        track.stop();
      });
      localStreamRef.current.addTrack(replacementTrack);

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = null;
        localVideoRef.current.srcObject = localStreamRef.current;
        await localVideoRef.current.play().catch(() => undefined);
      }
      updateCameraFacingMode(nextFacingMode);
    } catch (err) {
      console.error('Failed to switch camera:', err);
      setError('Unable to switch camera on this device.');
    }
  }, [isVideoOff, updateCameraFacingMode]);

  // Cleanup resources
  const cleanup = () => {
    stopCallTimer();
    
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
    }
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = null;
    }
    
    // Reset queues
    pendingOfferRef.current = null;
    pendingIceCandidatesRef.current = [];
    handlersRegisteredRef.current = false;
    updateCameraFacingMode('user');
    
    sessionStorage.removeItem('anonymousCallHostConnectionId');
    sessionStorage.removeItem('anonymousCallGuestConnectionId');
  };

  // Re-attach streams to video/audio DOM elements after modal renders
  // ontrack fires during setRemoteDescription BEFORE callState becomes 'connected',
  // so the modal's video/audio elements don't exist yet. This effect re-attaches
  // the stored streams once the modal is rendered and refs are available.
  useEffect(() => {
    if (callState === 'connected') {
      const attachStreams = () => {
        if (remoteStreamRef.current) {
          if (remoteVideoRef.current && !remoteVideoRef.current.srcObject) {
            console.log('Re-attaching remote stream to video element');
            remoteVideoRef.current.srcObject = remoteStreamRef.current;
          }
          if (remoteAudioRef.current && !remoteAudioRef.current.srcObject) {
            console.log('Re-attaching remote stream to audio element');
            remoteAudioRef.current.srcObject = remoteStreamRef.current;
          }
        }
        if (localStreamRef.current) {
          if (localVideoRef.current && !localVideoRef.current.srcObject) {
            console.log('Re-attaching local stream to video element');
            localVideoRef.current.srcObject = localStreamRef.current;
          }
        }
      };
      // Attach immediately (refs should be set after React commits the render)
      attachStreams();
      // Also try after a short delay for any edge cases with DOM mounting
      const timer = setTimeout(attachStreams, 300);
      return () => clearTimeout(timer);
    }
  }, [callState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
      if (connectionRef.current) {
        connectionRef.current.stop();
      }
    };
  }, []);

  return {
    callState,
    callKey,
    role,
    error,
    callTimer,
    isMuted,
    isVideoOff,
    cameraFacingMode,
    remoteName,
    localVideoRef,
    remoteVideoRef,
    remoteAudioRef,
    createSession,
    joinSession,
    endCall,
    toggleMute,
    toggleVideo,
    switchCamera,
    setError
  };
};
